// src/components/TiptapEditor.tsx
import { useEditor, EditorContent } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import { BubbleMenu } from '@tiptap/react/menus'

interface TiptapEditorProps {
  content?: string          // initial HTML or JSON
  onChange?: (html: string) => void  // callback when content changes
  editable?: boolean        // read-only mode for viewing
}

const TiptapEditor = ({
  content = '<p>Start writing your thoughts here...</p>',
  onChange,
  editable = true,
}: TiptapEditorProps) => {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3] },
      }),
    ],
    content,
    editable,
    onUpdate: ({ editor }) => {
      if (onChange) onChange(editor.getHTML())
    },
    immediatelyRender: false, // good for SSR/Firebase
  })

  if (!editor) return null

  return (
    <div className="tiptap-editor border rounded p-4 min-h-[300px] prose max-w-none relative">
      <EditorContent editor={editor} />

      {/* ← BubbleMenu goes here – it will float over selections */}
      <BubbleMenu 
        editor={editor} 
        shouldShow={({ editor }) => editor.isActive('text') || editor.isActive('heading')}
        {...({ tippyOptions: { duration: 100, placement: 'top' }} as any)} // optional: smoother appearance
      >
        {/* Your custom toolbar buttons – keep it simple for MVP */}
        <div className="flex items-center gap-1 bg-white shadow-md border rounded px-2 py-1">
          <button
            onClick={() => editor.chain().focus().toggleBold().run()}
            className={`p-1 rounded hover:bg-gray-100 ${editor.isActive('bold') ? 'bg-gray-200 font-bold' : ''}`}
          >
            Bold
          </button>
          <button
            onClick={() => editor.chain().focus().toggleItalic().run()}
            className={`p-1 rounded hover:bg-gray-100 ${editor.isActive('italic') ? 'bg-gray-200 italic' : ''}`}
          >
            Italic
          </button>
          <button
            onClick={() => editor.chain().focus().toggleStrike().run()}
            className={`p-1 rounded hover:bg-gray-100 ${editor.isActive('strike') ? 'bg-gray-200 line-through' : ''}`}
          >
            Strike
          </button>
          {/* Add more: underline, code, link, etc. later */}
        </div>
      </BubbleMenu>
    </div>
  )
}

export default TiptapEditor